import type { Move } from "../../../royalur/model/Move.js";
import { rand, randShuffle } from "../../../util/random.js";
import type { Game } from "../../../royalur/Game.js";
import { Agent } from "../../../royalur/agent/Agent.js";


/**
 * The strategy of the easy agent on RoyalUr.net. It intentionally
 * plays badly, but hopefully in a way that is still fun to beat.
 */
export class EasyAgent extends Agent {
    private static readonly RANDOM_ORDER_CHANCE = 0.3;
    private static readonly SCORE_CHANCE = 0.9;
    private static readonly CAPTURE_CHANCE = 0.25;
    private static readonly ROSETTE_CHANCE = 0.25;
    private static readonly MOVE_OFF_ROSETTE_CHANCE = 0.75;

    orderMoves(moves: Move[]) {
        // Sometimes, order moves randomly
        // (this is a better strategy, believe it or not).
        if (rand() < EasyAgent.RANDOM_ORDER_CHANCE) {
            randShuffle(moves);
            return;
        }

        // Less advanced pieces have lower path indices.
        const computePathIndex = (move: Move) => {
            return move.isIntroduction() ? -1 : move.getSourcePiece().getPathIndex();
        };

        // Sort so that least advanced pieces come first.
        moves.sort((a: Move, b: Move) => {
            return computePathIndex(a) - computePathIndex(b);
        });
    }

    override decideMove(game: Game, availableMoves: Move[]): Move {
        if (availableMoves.length === 0)
            throw new Error("No moves available");
        if (availableMoves.length === 1)
            return availableMoves[0];

        // Prioritise moves.
        availableMoves = [...availableMoves];
        this.orderMoves(availableMoves);

        // If they can score a piece, score it.
        if (rand() < EasyAgent.SCORE_CHANCE) {
            for (const move of availableMoves) {
                if (move.isScore())
                    return move;
            }
        }

        // If there is a capture, make it.
        if (rand() < EasyAgent.CAPTURE_CHANCE) {
            for (const move of availableMoves) {
                if (move.isCapture())
                    return move;
            }
        }

        // If there is a rosette to land on, land on it.
        if (rand() < EasyAgent.ROSETTE_CHANCE) {
            const shape = game.getBoard().getShape();
            for (const move of availableMoves) {
                if (move.isLandingOnRosette(shape))
                    return move;
            }
        }

        // If there is a rosette to move off of, move off it.
        if (rand() < EasyAgent.MOVE_OFF_ROSETTE_CHANCE) {
            const shape = game.getBoard().getShape();
            for (const move of availableMoves) {
                if (!move.isIntroduction() && shape.isRosette(move.getSource()))
                    return move;
            }
        }

        // Make the first move according to the ordering.
        return availableMoves[0];
    }
}
